import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import {
  ArrowBack,
  Edit,
  AccountTree,
  Add,
  // FileCopy,
} from "@material-ui/icons";
import { Button, Grid, Typography, Snackbar, Divider } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useHistory } from "react-router-dom";
import _ from "lodash";

import ProductsList from "./products/ProductsList";
import MatchProductsDialog from "./products/MatchProductsDialog";
import { fetchSupplierData } from "./products/productsSB.functions";
import DealsView from "../../admin-section/deals/DealsView";

/**
 * AdminSupplier component displays supplier details and a list of products for a given supplier.
 *
 * @component
 * @param {object} props - The component props.
 * @param {object} props.match - The match object from react-router.
 *
 * @returns {React.Element} The rendered AdminSupplier component.
 */
export default function AdminSupplier({ match }) {
  const history = useHistory();
  const supplierId = match.params.id;

  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState({});
  const [productsImport, setProductsImport] = useState([]);
  const [productsMaster, setProductsMaster] = useState([]);
  const [matchProductsDialogOpen, setMatchProductsDialogOpen] = useState(false);
  const [showUnassignedProductsAlert, setShowUnassignedProductsAlert] =
    useState(false);
  const [alertMessage, setAlertMessage] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // STATE HANDLERS

  // matchProductDialogOpen
  const openMatchProductsDialog = () => {
    setMatchProductsDialogOpen(true);
  };

  const closeMatchProductsDialog = () => {
    setMatchProductsDialogOpen(false);
    fetchSupplier();
  };

  // productsImport
  const assignProductToImport = (importProduct, masterProduct) => {
    setProductsImport(prevState =>
      prevState.map(item => {
        return item.id === importProduct
          ? { ...item, products_master_id: masterProduct }
          : item;
      })
    );
  };

  const handleAlert = (message, severity) => {
    setAlertMessage({ message, severity, open: true });
  };

  const closeAlert = () => {
    setAlertMessage(prev => ({ ...prev, open: false }));
  };

  // FETCH CALLS
  const fetchSupplier = async () => {
    if (supplierId) {
      setLoading(true);
      try {
        const fetchSupplierDataResult = await fetchSupplierData(supplierId);

        if (fetchSupplierDataResult.error !== 0) {
          throw new Error(fetchSupplierDataResult.error);
        } else {
          const { products_import, products_master } =
            fetchSupplierDataResult.data;
          const pureSupplier = _.omit(fetchSupplierDataResult?.data, [
            "products_import",
            "products_master",
          ]);

          const unassignedProducts = products_import.filter(
            item => item.products_master_id === null
          );

          if (unassignedProducts.length !== 0) {
            setShowUnassignedProductsAlert(true);
          }

          setSupplier(pureSupplier);
          setProductsImport(products_import);
          setProductsMaster(products_master);
        }
      } catch (error) {
        if (typeof error === "string") {
          handleAlert(error, "error");
        } else console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  // SIDE EFFECTS

  useEffect(() => {
    fetchSupplier();
  }, [supplierId]);

  return (
    <>
      {" "}
      <Button
        color="primary"
        startIcon={<ArrowBack />}
        onClick={() => history.push("/settings/Suppliers")}
        style={{
          marginBottom: 10,
        }}
      >
        Back
      </Button>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Typography align="left" variant="h2" style={{ fontWeight: "bold" }}>
            {supplier ? supplier.supplier_name : "Loading..."}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="h4">Actions</Typography>
        </Grid>
        <Grid item container spacing={2}>
          {showUnassignedProductsAlert && (
            <Grid item xs={12}>
              <Alert severity="error">
                Unmatched Products - Please match all products
              </Alert>
            </Grid>
          )}
          <Grid item>
            <Link to={`/edit-supplier/${match.params.id}`}>
              <Button startIcon={<Edit />} color="primary" variant="contained">
                Edit Supplier
              </Button>
            </Link>
          </Grid>
          <Grid item>
            <Button
              startIcon={<AccountTree />}
              color={showUnassignedProductsAlert ? "secondary" : "primary"}
              variant="contained"
              onClick={openMatchProductsDialog}
            >
              Match Products
            </Button>
          </Grid>
          <Grid item>
            <Link to={`/add-product/${supplierId}`}>
              <Button
                startIcon={<Add />}
                color="primary"
                variant="contained"
                onClick={() => {}}
              >
                Add Product
              </Button>
            </Link>
          </Grid>
        </Grid>
        <Divider style={{ margin: 30 }} />
        <Grid item>
          <Typography variant="h4">Products</Typography>
        </Grid>
        <Grid item>
          {!loading && (
            <ProductsList supplier={supplier} productsMaster={productsMaster} />
          )}
        </Grid>
      </Grid>
      <Divider style={{ margin: 30 }} />
      <DealsView supplierId={supplierId} />
      <Snackbar
        open={alertMessage.open}
        onClose={closeAlert}
        autoHideDuration={5000}
      >
        <Alert severity={alertMessage.severity}>{alertMessage.message}</Alert>
      </Snackbar>
      <MatchProductsDialog
        open={matchProductsDialogOpen}
        closeDialog={closeMatchProductsDialog}
        productsMaster={productsMaster}
        productsImport={productsImport}
        handleSelect={assignProductToImport}
        handleAlert={handleAlert}
      />
    </>
  );
}
